import React, { useEffect, useState } from 'react';
import { getAllMarketData } from '../../api/marketIntelligency';
import { Link } from 'react-router-dom';
import AddMarketIntelligencyModal from './AddMarketIntelligencyModal';

function TableMarket(props) {
  const [marketIntelligencyData, setMarketIntelligencyData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLocation, setFilterLocation] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleOrderClick = () => {
    setShowModal(true);
  };

  // Function to format date and remove time - handles ISO format
  const formatDateOnly = (dateTimeString) => {
    if (!dateTimeString) return '';
    
    try {
      // Handle ISO format like "2025-10-02T00:00:00.000Z"
      if (dateTimeString.includes('T')) {
        const datePart = dateTimeString.split('T')[0];
        return datePart;
      }
      
      // Handle space-separated format like "2025-10-02 14:30:22"
      if (dateTimeString.includes(' ')) {
        const datePart = dateTimeString.split(' ')[0];
        return datePart;
      }
      
      // If it's already just a date, return as is
      return dateTimeString;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateTimeString;
    }
  };

  function getDataTable() {
    setLoading(true);
    getAllMarketData((result) => {
      if (result.ok !== 1) {
        console.log(result);
        setLoading(false);
        return;
      }
      setMarketIntelligencyData(result.result);
      setLoading(false);
    });
  }

  useEffect(() => {
    getDataTable();
  }, []);

  const filteredData = marketIntelligencyData.filter(item => {
    const matchesSearch = item.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.market_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.market_location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.reg_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLocation = filterLocation === 'all' || 
                           item.market_location?.toLowerCase().includes(filterLocation.toLowerCase());
    return matchesSearch && matchesLocation;
  });

  // Get unique locations for filter
  const uniqueLocations = [...new Set(marketIntelligencyData.map(item => item.market_location))].filter(Boolean);

  // Calculate statistics
  const totalProducts = [...new Set(marketIntelligencyData.map(item => item.product_name))].length;
  const totalMarkets = [...new Set(marketIntelligencyData.map(item => item.market_name))].length;
  const totalLocations = uniqueLocations.length;

  const MarketCard = ({ item, index }) => (
    <div className="card border-0 shadow-sm mb-3">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="d-flex align-items-center flex-grow-1">
            <div className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{width: '40px', height: '40px'}}>
              <i className="bi bi-shop text-info"></i>
            </div>
            <div className="flex-grow-1 min-w-0">
              <h6 className="mb-1 fw-bold text-truncate">{item.product_name}</h6>
              <small className="text-muted d-block">
                <i className="bi bi-calendar3 me-1"></i>
                {formatDateOnly(item.reg_date)}
              </small>
            </div>
          </div>
          <div className="flex-shrink-0">
            <span className="badge bg-success fs-6">
              {item.price} / {item.unit}
            </span>
          </div>
        </div>

        <div className="row g-2 mb-3">
          <div className="col-6">
            <small className="text-muted d-block">Market</small>
            <small className="fw-medium">{item.market_name}</small>
          </div>
          <div className="col-6">
            <small className="text-muted d-block">Location</small>
            <small className="fw-medium">{item.market_location}</small>
          </div>
          <div className="col-12">
            <small className="text-muted d-block">Sales Contact</small>
            <div className="d-flex align-items-center">
              <i className="bi bi-person-circle me-2 text-muted"></i>
              <div>
                <small className="fw-medium d-block">{item.reg_name}</small>
                <a href={`tel:${item.reg_mobile}`} className="text-decoration-none small">
                  <i className="bi bi-telephone me-1"></i>
                  {item.reg_mobile}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container-fluid p-3 p-md-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <div className="row">
        <div className="col-12">
          {/* Header */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div>
                <h2 className="mb-1 fw-bold">Market Intelligence</h2>
                <p className="text-muted">Track market prices and product availability</p>
              </div>
              <button 
                onClick={handleOrderClick} 
                className="btn btn-primary"
              >
                <i className="bi bi-plus-circle me-2"></i>
                Update Market
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="row g-3 mb-4">
            <div className="col-12 col-sm-6 col-lg-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="flex-grow-1">
                      <p className="text-muted mb-1 small">Total Records</p>
                      <h3 className="mb-0 fw-bold">{marketIntelligencyData.length}</h3>
                    </div>
                    <div className="bg-primary bg-opacity-10 p-3 rounded flex-shrink-0">
                      <i className="bi bi-graph-up fs-4 text-primary"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-lg-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="flex-grow-1">
                      <p className="text-muted mb-1 small">Products</p>
                      <h3 className="mb-0 fw-bold text-success">{totalProducts}</h3>
                    </div>
                    <div className="bg-success bg-opacity-10 p-3 rounded flex-shrink-0">
                      <i className="bi bi-box-seam fs-4 text-success"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-lg-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="flex-grow-1">
                      <p className="text-muted mb-1 small">Markets</p>
                      <h3 className="mb-0 fw-bold text-info">{totalMarkets}</h3>
                    </div>
                    <div className="bg-info bg-opacity-10 p-3 rounded flex-shrink-0">
                      <i className="bi bi-shop fs-4 text-info"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-lg-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="flex-grow-1">
                      <p className="text-muted mb-1 small">Locations</p>
                      <h3 className="mb-0 fw-bold text-warning">{totalLocations}</h3>
                    </div>
                    <div className="bg-warning bg-opacity-10 p-3 rounded flex-shrink-0">
                      <i className="bi bi-geo-alt fs-4 text-warning"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-12 col-md-8">
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0">
                      <i className="bi bi-search text-muted"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0 ps-0"
                      placeholder="Search by product, market, location, or contact..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                <div className="col-12 col-md-4">
                  <select
                    className="form-select"
                    value={filterLocation}
                    onChange={(e) => setFilterLocation(e.target.value)}
                  >
                    <option value="all">All Locations</option>
                    {uniqueLocations.map((location, index) => (
                      <option key={index} value={location}>{location}</option>
                    ))}
                  </select>
                </div>

                <div className="col-12">
                  <div className="d-flex gap-2 flex-wrap align-items-center">
                    <button
                      onClick={getDataTable}
                      disabled={loading}
                      className="btn btn-sm btn-outline-secondary"
                      title="Refresh"
                    >
                      <i className={`bi bi-arrow-clockwise ${loading ? 'spinner-border spinner-border-sm' : ''}`}></i>
                      {loading ? ' Loading...' : ' Refresh'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-graph-down fs-1 text-muted"></i>
              <p className="text-muted mt-2">No market data found</p>
            </div>
          ) : (
            <>
              {/* Mobile Card View (visible on small and medium screens) */}
              <div className="d-block d-xl-none">
                {filteredData.map((item, index) => (
                  <MarketCard key={index} item={item} index={index} />
                ))}
              </div>

              {/* Desktop Table View (visible on extra large screens) */}
              <div className="card border-0 shadow-sm d-none d-xl-block">
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="fw-semibold text-uppercase small border-0" style={{minWidth: '100px'}}>Date</th>
                          <th className="fw-semibold text-uppercase small border-0" style={{minWidth: '150px'}}>Product</th>
                          <th className="fw-semibold text-uppercase small border-0" style={{minWidth: '120px'}}>Price</th>
                          <th className="fw-semibold text-uppercase small border-0" style={{minWidth: '150px'}}>Market</th>
                          <th className="fw-semibold text-uppercase small border-0" style={{minWidth: '120px'}}>Location</th>
                          <th className="fw-semibold text-uppercase small border-0" style={{minWidth: '180px'}}>Sales Contact</th>
                          <th className="fw-semibold text-uppercase small border-0" style={{minWidth: '100px'}}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredData.map((item, index) => (
                          <tr key={index}>
                            <td className="text-nowrap">
                              <small className="text-muted">
                                <i className="bi bi-calendar3 me-1"></i>
                                {formatDateOnly(item.reg_date)}
                              </small>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-2 flex-shrink-0" style={{width: '32px', height: '32px'}}>
                                  <i className="bi bi-box-seam text-success small"></i>
                                </div>
                                <span className="fw-medium text-truncate" style={{maxWidth: '120px'}} title={item.product_name}>
                                  {item.product_name}
                                </span>
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-success bg-opacity-10 text-success fw-medium">
                                {item.price} / {item.unit}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-2 flex-shrink-0" style={{width: '32px', height: '32px'}}>
                                  <i className="bi bi-shop text-info small"></i>
                                </div>
                                <span className="text-truncate" style={{maxWidth: '120px'}} title={item.market_name}>
                                  {item.market_name}
                                </span>
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-warning bg-opacity-10 text-warning">
                                <i className="bi bi-geo-alt me-1"></i>
                                {item.market_location}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-2 flex-shrink-0" style={{width: '32px', height: '32px'}}>
                                  <i className="bi bi-person text-primary small"></i>
                                </div>
                                <div className="flex-grow-1 min-w-0">
                                  <div className="fw-medium text-truncate" style={{maxWidth: '100px'}} title={item.reg_name}>
                                    {item.reg_name}
                                  </div>
                                  <a href={`tel:${item.reg_mobile}`} className="text-decoration-none small text-muted">
                                    <i className="bi bi-telephone me-1"></i>
                                    {item.reg_mobile}
                                  </a>
                                </div>
                              </div>
                            </td>
                            <td>
                              <button className="btn btn-sm btn-outline-secondary" disabled>
                                <i className="bi bi-three-dots"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Footer */}
          <div className="mt-3 text-center text-muted small">
            Showing {filteredData.length} of {marketIntelligencyData.length} market records
          </div>
        </div>
      </div>

      <AddMarketIntelligencyModal 
        show={showModal} 
        handleClose={handleCloseModal} 
      />
    </div>
  );
}

export default TableMarket;